import React, { useState, useEffect } from "react";

import { useHistory } from "react-router-dom";
import { isEmpty } from "lodash";
import { Trans, useTranslation } from "react-i18next";

import LoadingOverlay from "../../components/Loaders/LoadingOverlay";
import Pagination from "../../components/Pagination/Pagination";
import ModelTable from "../../components/Tables/ModelTable";
import FilterDropdown from "../../components/Dropdown/FilterDropdown";
import { getCurrentDateYMD } from "../../utils/DateHelper";
import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";
import ModelsAPI from "../../API/models.api";
import ModelParametersAPI from "../../API/modelParameters.api";
import LookupsAPI from "../../API/lookups.api";
import { getRoles } from "../../utils/OktaHelpers";
import { MODEL_STATUS, ROLES } from "../../utils/constants";
import { isAuthorized } from "../../utils/RolesHelpers";
import { getSessionFilters, getMappedFilters } from "../../utils/FilterHelpers";
import LogCapture from "../../utils/LogManagement/LogHelper";
import { ACTIONS, ENTITIES } from "../../utils/LogManagement/LogConstants";
import BaseAlertModal from "../../components/Modals/BaseAlertModal";
import ModelBulkUploadModal from "../../components/Modals/ModelBulkUploadModal";
import DropdownButton from "../../components/Buttons/DropdownButton";
import { setObject, getObject } from "../../utils/StorageHelpers";
import { mapLookupToSelect } from "../../utils/Helpers";
import MissingData from "../../components/Shared/MissingData";

const Model = ({ currentUser }) => {
    const [isRendered, setIsRendered] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [isModelsLoading, setIsModelsLoading] = useState(true);
    const [curretUserRoles, setCurretUserRoles] = useState([]);
    //Models states
    const [models, setModels] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [modelStatuses, setModelStatuses] = useState([]);
    const [modelTotalCount, setModelTotalCount] = useState(0);
    const [modelCurrentPage, setModelCurrentPage] = useState(1);
    const [modelFiltersUsed, setModelFiltersUsed] = useState(false);
    const [modelFilters, setModelFilters] = useState({
        modelName: "",
        parameterName: "",
        materialName: "",
        status: "",
    });
    //React lazy initialization in to reduce the cost of useless calling of getSessionFilters
    const [modelAppliedFilters, setModelAppliedFilters] = useState(() =>
        getSessionFilters("model-table-filters")
    );
    const [modelSortBy, setModelSortBy] = useState(undefined);
    const [modelSortDirection, setModelSortDirection] = useState(undefined);

    const [bulkModelUploadLoading, setBulkModelUploadLoading] = useState(false);
    const [deployedMaterialParameters, setDeployedMaterialParameters] =
        useState([]);

    const [bulkUploadModal, setBulkUploadModal] = useState(false);
    const [forceDeployModal, setForceDeployModal] = useState(false);
    const [forcedModel, setForceModel] = useState(null);

    const { t } = useTranslation();

    const history = useHistory();

    const [itemsPerRequest, setItemsPerRequest] = useState(
        () => getObject("Models-itemsPerPage", true) || 10
    );

    const headers = [
        { label: t("name"), name: "modelName" },
        { label: t("materialName"), name: "materialName" },
        { label: t("createdAt"), name: "createdAt" },
        { label: t("createdBy"), name: "createdBy" },
        {
            label: t("affiliate"),
            name: "organization",
            roles: !isAuthorized([ROLES.superAdmin], curretUserRoles),
        },
        {
            label: t("action"),
            skipSort: true,
            name: "action",
            roles: !isAuthorized(
                [ROLES.support, ROLES.partnerAdmin, ROLES.superAdmin],
                curretUserRoles
            ),
        },
    ];

    const subHeaders = [
        t("parameterName"),
        t("version"),
        t("publishedAt"),
        t("status"),
        t("actions"),
    ];

    const modelSearchFilters = [
        {
            label: t("modelName"),
            name: "modelName",
            type: "Input",
            placeholder: t("modelNamePlaceholder"),
        },
        {
            label: t("materialName"),
            name: "materialName",
            type: "Input",
            placeholder: t("materialNamePlaceholder"),
        },
        {
            label: `${t("parameter")} ${t("technicalName")}`,
            name: "parameterName",
            type: "Input",
            placeholder: t("parameterNamePlaceholder"),
        },
        {
            label: t("status"),
            name: "status",
            type: "Dropdown",
            options: modelStatuses,
        },
        {
            label: t("createdAt"),
            name: "createdAt",
            type: "Date",
            max: getCurrentDateYMD(),
        },
        {
            label: t("affiliateName"),
            name: "organizationId",
            type: "Dropdown",
            options: organizations,
            disabled: !isAuthorized([ROLES.superAdmin], curretUserRoles),
        },
    ];

    const dropdownButtonOptions = [
        {
            label: t("standardModel"),
            callback: () => history.push("/models/create"),
        },
        {
            label: t("customModel"),
            callback: () => history.push("/models/create/custom"),
        },
    ];

    useEffect(() => {
        const modelLocalStorageFilters = getSessionFilters(
            "model-table-filters"
        );
        if (!isEmpty(modelLocalStorageFilters)) {
            setModelFilters(modelLocalStorageFilters);
            setModelAppliedFilters(modelLocalStorageFilters);
        }

        fetchModelStatuses();
        fetchOrganizations();
        setCurretUserRoles(getRoles());
        setIsRendered(true);
        getDeployedMaterialParameters();
    }, []);

    const getData = () => {
        fetchData(modelAppliedFilters, modelSortBy, modelSortDirection);
    };

    useEffect(() => {
        if (isRendered) {
            if (modelSortBy && modelSortDirection) {
                setModelCurrentPage(1);
                fetchData(modelAppliedFilters, modelSortBy, modelSortDirection);
            } else {
                setModelCurrentPage(1);
                fetchData(modelAppliedFilters);
            }
        }
    }, [modelSortBy, modelSortDirection, isRendered]);

    const fetchOrganizations = async () => {
        let result = await LookupsAPI.getOrganizations();
        if (result) {
            setOrganizations(mapLookupToSelect(result));
        }
    };

    const fetchData = async (
        _filters = null,
        _sortBy = null,
        _sortDirection = null,
        offset = 0,
        itemsPerPage = itemsPerRequest
    ) => {
        setIsModelsLoading(true);
        const newFilter = getMappedFilters(_filters);
        const _offset = offset < 0 ? 0 : offset;

        let result = await ModelsAPI.getModels(
            itemsPerPage,
            newFilter,
            _offset,
            _sortBy,
            _sortDirection
        );

        setModels(result?.data || []);
        setModelTotalCount(result?.totalCount || 0);
        setIsModelsLoading(false);
        if (initialLoading) {
            setInitialLoading(false);
        }
        // ErrorAlert(t("couldNotGetModelsMessage"));
    };

    const getPageAfterDelete = () => {
        if (
            models.length === 1 &&
            modelCurrentPage === Math.ceil(modelTotalCount / itemsPerRequest)
        ) {
            return Math.max(1, modelCurrentPage - 1);
        } else {
            return Math.max(1, modelCurrentPage);
        }
    };

    const fetchModelStatuses = async () => {
        let result = await LookupsAPI.getModelStatuses();
        if (result) {
            setModelStatuses(mapLookupToSelect(result));
        }
    };

    const getDeployedMaterialParameters = async () => {
        setBulkModelUploadLoading(true);
        const result = await LookupsAPI.getDeployedMaterialParameters();
        setDeployedMaterialParameters(result);
        setBulkModelUploadLoading(false);
    };

    const applyModelFilters = (applyFilters) => {
        fetchData(applyFilters, modelSortBy, modelSortDirection);
        setModelFiltersUsed(true);
    };

    const handleModelPageClick = (index) => {
        if (index !== modelCurrentPage) {
            setModelCurrentPage(index);
            // call API to get models with the next pagination
            fetchData(modelFilters, modelSortBy, modelSortDirection, index - 1);
        }
    };

    const handleDeleteModel = async (parameterRow) => {
        let referenceModel = models.find((model) => {
            return model.id === parameterRow.modelId;
        });

        let result = await ModelParametersAPI.deleteModel(parameterRow.id);
        if (result?.deleted) {
            SuccessAlert(t("deleteModelSuccessMessage"));
            LogCapture.info(
                `model parameter with id: ${parameterRow.id} is deleted`,
                ENTITIES.modelParameters,
                ACTIONS.modelParameters.delete
            );
            let newCurrentPage =
                referenceModel.modelParameters.length > 1
                    ? modelCurrentPage
                    : getPageAfterDelete();
            setModelCurrentPage(newCurrentPage);
            if (referenceModel.modelParameters.length <= 1) {
                fetchData(
                    modelFilters,
                    modelSortBy,
                    modelSortDirection,
                    newCurrentPage - 1
                );
            } else {
                const newModels = [...models];
                let modelIndex = newModels.findIndex(
                    (model) => model.id === parameterRow.modelId
                );
                let innerModelIndex = newModels[
                    modelIndex
                ].modelParameters.findIndex((model) => {
                    return model.id === parameterRow.id;
                });
                newModels[modelIndex].modelParameters.splice(
                    innerModelIndex,
                    1
                );
                setModels(newModels);
            }
            return true;
        } else {
            // ErrorAlert(t("deleteModelFailMessage"));
            return false;
        }
    };

    const changeDeployedStatus = (group, selectedModel) => {
        let tempModels = [...models];
        tempModels.forEach(
            ({ materialName, organization, modelParameters }) => {
                if (
                    materialName === group.materialName &&
                    organization === group.organization
                ) {
                    modelParameters.forEach((innerModel) => {
                        if (
                            innerModel.id !== selectedModel.id &&
                            innerModel.parameterName ===
                                selectedModel.parameterName &&
                            innerModel.deployed
                        ) {
                            innerModel.deployed = false;
                            innerModel.status = MODEL_STATUS.completed;
                        }
                    });
                }
            }
        );

        return tempModels;
    };

    const handleDeployModel = async (group, subRow, force = false) => {
        try {
            let selectedModel = subRow;
            let result = await ModelParametersAPI.deployModel({
                id: selectedModel?.id,
                force,
            });

            if (result?.modified) {
                //Deploy or undeploy the model
                selectedModel.deployed = !selectedModel?.deployed;
                selectedModel.status = selectedModel?.deployed
                    ? MODEL_STATUS.deployed
                    : MODEL_STATUS.completed;

                //loop over models to change the status of other deployed models
                //with the same material, parameter and organization
                const tempModels = changeDeployedStatus(group, selectedModel);
                setModels(tempModels);
                //the below condition is reversed becuase it's already toggled
                LogCapture.info(
                    `Model ${
                        selectedModel?.deployed ? "undeploy" : "deploy"
                    } with id: ${selectedModel?.id}`,
                    ENTITIES.modelParameters,
                    selectedModel?.deployed
                        ? ACTIONS.modelParameters.undeployModel
                        : ACTIONS.modelParameters.deployModel
                );
                getDeployedMaterialParameters();
            } else if (result.conflict) {
                setForceDeployModal(true);
                setForceModel({ group, subRow });
            }
        } catch (error) {
            LogCapture.error(
                `Couldn't ${
                    selectedModel?.deployed ? "undeploy" : "deploy"
                } model with id: ${selectedModel?.id}, error: ${error}`
            );
            ErrorAlert(error?.message || error);
        }
    };

    const isLoading = () => {
        if (isModelsLoading) return true;
        return false;
    };

    const forceModalSubmitAction = () => {
        return handleDeployModel(forcedModel.group, forcedModel.subRow, true);
    };

    const handleItemsPerPage = (itemsPerPage) => {
        setItemsPerRequest(itemsPerPage);
        setModelCurrentPage(1);
        setObject("Models-itemsPerPage", itemsPerPage, true);
        // call API to update the items per page
        fetchData(
            modelFilters,
            modelSortBy,
            modelSortDirection,
            0,
            itemsPerPage
        );
    };

    return (
        <>
            <div id="model-content" className="model-content">
                <header className="main-content-header flex items-center justify-between">
                    <h1 className="h1">
                        <Trans i18nKey="models">Models</Trans>
                    </h1>
                    <div className="action-header-wrapper flex items-center gap-2">
                        <FilterDropdown
                            filterOptions={modelSearchFilters}
                            filters={modelFilters}
                            setFilters={setModelFilters}
                            applyFilters={applyModelFilters}
                            component={"model-table"}
                            appliedFilters={modelAppliedFilters}
                            setAppliedFilters={setModelAppliedFilters}
                            disabled={isLoading()}
                        />
                        {isAuthorized(
                            [ROLES.partnerAdmin, ROLES.support],
                            curretUserRoles
                        ) && (
                            <>
                                <button
                                    onClick={() => setBulkUploadModal(true)}
                                    className="default-orange-outline-btn flex items-center gap-2"
                                    id="upload-model-btn"
                                >
                                    <i className="fas fa-upload" />
                                    <Trans i18nKey="uploadModels">
                                        Upload Models
                                    </Trans>
                                </button>
                                <DropdownButton
                                    buttonName={t("addNewModel")}
                                    options={dropdownButtonOptions}
                                    icon="fas fa-plus"
                                    optionsClassNames="rounded-none py-1.5"
                                />
                            </>
                        )}
                    </div>
                </header>
            </div>
            <LoadingOverlay active={isLoading()}>
                <div className="card-table shadow">
                    {models?.length > 0 ? (
                        <>
                            <ModelTable
                                headers={headers}
                                subHeaders={subHeaders}
                                rows={models}
                                handleDelete={handleDeleteModel}
                                handleDeploy={handleDeployModel}
                                sortBy={modelSortBy}
                                setSortBy={setModelSortBy}
                                sortDirection={modelSortDirection}
                                setSortDirection={setModelSortDirection}
                                currentPage={modelCurrentPage}
                                currentUser={currentUser}
                                getData={getData}
                            />
                            <Pagination
                                currentPage={modelCurrentPage}
                                setCurrentPage={handleModelPageClick}
                                totalCount={modelTotalCount}
                                sizePerPage={itemsPerRequest}
                                handleItemsPerPage={handleItemsPerPage}
                            />
                        </>
                    ) : (
                        <MissingData
                            isFilterUsed={modelFiltersUsed}
                            entity={t("models")}
                            includeText={!isLoading()}
                            path="/models/create"
                        />
                    )}
                </div>
            </LoadingOverlay>

            <BaseAlertModal
                isVisible={forceDeployModal}
                setIsVisible={setForceDeployModal}
                title={t("forceDeployModalTitle")}
                message={t("forceDeployModalMessage")}
                action={{
                    submitAction: forceModalSubmitAction,
                }}
                type="warning"
            />
            <ModelBulkUploadModal
                isVisible={bulkUploadModal}
                setIsVisible={setBulkUploadModal}
                isLoading={bulkModelUploadLoading}
                setIsLoading={setBulkModelUploadLoading}
                deployedMaterialParameters={deployedMaterialParameters}
            />
        </>
    );
};

export default Model;
